import { useMsal } from '@azure/msal-react';
import { makeStyles, shorthands } from '@fluentui/react-components';
import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector } from '../../redux/app/hooks';
import { getSelectedChatID, RootState } from '../../redux/app/store';
import { ChatList } from '../chat/chat-list/ChatList';
import { AgentLogService, AgentLog } from '../../libs/services/AgentLogService';
import { useDispatch } from 'react-redux';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { AlertType } from '../../libs/models/AlertType';
import { addAlert } from '../../redux/features/app/appSlice';
import { ChatWindow } from '../chat/ChatWindow';
import { AgentSelectListItem } from '../../libs/services/AgentService';
import { useBoolean } from '../../libs/hooks/useBoolean';
import { AgentLogsPanel } from '../agent-log/AgentLogsPanel';

const useClasses = makeStyles({
    container: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'start',
        height: '100%',
        position: 'relative',
    },
    sidePanel: {
        width: '620px',
        background: '#f0f0f0',
        borderLeft: '1px solid #ddd',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
    },
});

export const ChatView: FC<{
    selectedAgentId: string;
    setSelectedAgentId: (id: string) => void;
    agents: AgentSelectListItem[];
    setLoadingState?: () => void;
}> = ({ selectedAgentId, setSelectedAgentId, agents, setLoadingState }) => {
    const classes = useClasses();
    const dispatch = useDispatch();
    const { selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { instance, inProgress } = useMsal();

    const panelOpen = useBoolean();
    const editingTitle = useBoolean();

    const [agentLogs, setAgentLogs] = useState<AgentLog[]>([]);
    // null backend'den "logları görüntüleme yetkiniz yok" sinyalidir (admin değil).
    // Array (boş dahi olsa) yetki olduğunu gösterir ve panel kullanılabilir.
    const [canViewLogs, setCanViewLogs] = useState<boolean>(false);
    const agentLogsEndRef = useRef<HTMLDivElement>(null);

    const fetchLogs = useCallback(async () => {
        const chatId = getSelectedChatID();
        if (!selectedAgentId || !chatId) {
            // Henüz id'ler hazır değil; boş URL ile 404 üretmemek için bekle.
            return;
        }
        try {
            const service = new AgentLogService();
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            const data = await service.getAgentLogs(`/agent-logs/${selectedAgentId}/${chatId}`, accessToken);
            // Backend array döndürürse yetki var; null / 204 / başka bir şey ise yetki yok.
            if (!Array.isArray(data)) {
                setCanViewLogs(false);
                setAgentLogs([]);
                return;
            }
            setCanViewLogs(true);
            setAgentLogs(data);
        } catch {
            setAgentLogs([]);
            dispatch(
                addAlert({
                    message: 'Failed to load agent logs. Please try again.',
                    type: AlertType.Error,
                }),
            );
        }
    }, [instance, inProgress, dispatch, selectedAgentId]);

    useEffect(() => {
        if (panelOpen.value) {
            void fetchLogs();
        }
    }, [panelOpen.value, fetchLogs]);

    useEffect(() => {
        if (panelOpen.value && agentLogsEndRef.current) {
            agentLogsEndRef.current.scrollTop = agentLogsEndRef.current.scrollHeight;
        }
    }, [agentLogs, panelOpen.value]);

    useEffect(() => {
        if (selectedId) {
            void fetchLogs();
        }
    }, [selectedId, fetchLogs]);

    const [isOpen, setIsOpen] = useState(false);

    const setMenuOpen = () => {
        setIsOpen((prev) => !prev);
    };

    return (
        <div className={classes.container}>
            <ChatList
                selectedAgentId={selectedAgentId}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setLoadingState={setLoadingState}
            />
            {selectedId !== '' && (
                <ChatWindow
                    setIsOpen={setMenuOpen}
                    selectedAgentId={selectedAgentId}
                    setSelectedAgentId={setSelectedAgentId}
                    agents={agents}
                    panelOpen={panelOpen.value}
                    setPanelOpen={canViewLogs ? panelOpen.setValue : undefined}
                    onMessageSent={() => void fetchLogs()}
                    onEditTitleClick={editingTitle.onTrue}
                />
            )}
            {canViewLogs && (
                <AgentLogsPanel
                    isOpen={panelOpen.value}
                    agentLogs={agentLogs}
                    agentLogsEndRef={agentLogsEndRef}
                    onClose={panelOpen.onFalse}
                />
            )}
        </div>
    );
};
